/**
 * ncrmStagingApi.mjs
 * Thin axios wrappers for the ncrm/workflows/staging REST API.
 * Keeps page components free of direct axios mutation calls (enforced by tests).
 */

import axios from "axios";

const BASE = "/ncrm/workflows/staging";

export const fetchStagingRecords = (params = {}) =>
  axios.get(BASE, { params });

export const fetchStagingRecord = (id, workflowInstanceId) =>
  axios.get(`${BASE}/${id}`, {
    params: { workflow_instance_id: workflowInstanceId },
  });

export const importStagingFromEndpoint = (endpointId, deliveryIds = null, workflowInstanceId = null) =>
  axios.post(`${BASE}/import`, {
    endpoint_id: endpointId,
    workflow_instance_id: workflowInstanceId,
    ...(deliveryIds ? { delivery_ids: deliveryIds } : {}),
  });

export const updateStagingStatus = (id, status, reviewNotes = undefined, workflowInstanceId = null, options = {}) =>
  axios.patch(`${BASE}/${id}/status`, {
    status,
    workflow_instance_id: workflowInstanceId,
    ...(reviewNotes !== undefined ? { review_notes: reviewNotes } : {}),
    ...(Object.prototype.hasOwnProperty.call(options, "matched_company_id")
      ? { matched_company_id: options.matched_company_id }
      : {}),
  });

export const runDedupForStagingRecord = (id, options = {}) =>
  axios.post(`${BASE}/${id}/dedup`, options);

export const runDedupForWorkflow = (workflowInstanceId, stagingIds = null) =>
  axios.post(`${BASE}/dedup`, {
    workflow_instance_id: workflowInstanceId,
    ...(stagingIds ? { staging_ids: stagingIds } : {}),
  });

export const runMailjetTriggerForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/mailjet-trigger`, {
    workflow_instance_id: workflowInstanceId,
  });

export const runContactExistenceForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/contact-existence-check`, {
    workflow_instance_id: workflowInstanceId,
  });

export const runLeadPresenceForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/lead-presence-check`, {
    workflow_instance_id: workflowInstanceId,
  });

export const runAiCompatibilityForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/ai-compatibility-check`, {
    workflow_instance_id: workflowInstanceId,
  });

export const runMailjetLeadAddForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/mailjet-lead-add`, {
    workflow_instance_id: workflowInstanceId,
  });

export const runMailjetTelegramNotifyForWorkflow = (workflowInstanceId) =>
  axios.post(`${BASE}/mailjet-telegram-notify`, {
    workflow_instance_id: workflowInstanceId,
  });

export const importMailjetCsv = (workflowInstanceId, file) => {
  const formData = new FormData();
  formData.append("workflow_instance_id", workflowInstanceId);
  formData.append("file", file);
  return axios.post(`${BASE}/mailjet-csv-import`, formData);
};

export const runEnrichmentForStagingRecord = (id, workflowInstanceId, options = {}) =>
  axios.post(`${BASE}/${id}/enrichment`, {
    workflow_instance_id: workflowInstanceId,
    ...options,
  });

export const runEnrichmentForWorkflow = (workflowInstanceId, stagingIds = null) =>
  axios.post(`${BASE}/enrichment`, {
    workflow_instance_id: workflowInstanceId,
    ...(stagingIds ? { staging_ids: stagingIds } : {}),
  });

export const syncEnrichmentForStagingRecord = (id, workflowInstanceId) =>
  axios.post(`${BASE}/${id}/enrichment/sync`, {
    workflow_instance_id: workflowInstanceId,
  });

export const syncEnrichmentForWorkflow = (workflowInstanceId, stagingIds = null) =>
  axios.post(`${BASE}/enrichment/sync`, {
    workflow_instance_id: workflowInstanceId,
    ...(stagingIds ? { staging_ids: stagingIds } : {}),
  });

export const reviewEnrichmentSuggestions = (id, workflowInstanceId, decisions = []) =>
  axios.patch(`${BASE}/${id}/enrichment/review`, {
    workflow_instance_id: workflowInstanceId,
    decisions,
  });

export const recheckEnrichmentDedupForStagingRecord = (id, workflowInstanceId) =>
  axios.post(`${BASE}/${id}/enrichment/dedup-recheck`, {
    workflow_instance_id: workflowInstanceId,
  });

export const writeStagingRecord = (id, workflowInstanceId = null) =>
  axios.post(`${BASE}/${id}/write`, {
    workflow_instance_id: workflowInstanceId,
  });

export const writeApprovedStagingRecords = (workflowInstanceId, stagingIds = null) =>
  axios.post(`${BASE}/write-approved`, {
    workflow_instance_id: workflowInstanceId,
    ...(Array.isArray(stagingIds) && stagingIds.length ? { staging_ids: stagingIds } : {}),
  });

export const addStagingRecordToLeadBoard = (id, workflowInstanceId = null, options = {}) =>
  axios.post(`${BASE}/${id}/lead-board-add`, {
    workflow_instance_id: workflowInstanceId,
    ...(options.boardId ? { board_id: options.boardId } : {}),
    ...(options.stageId ? { stage_id: options.stageId } : {}),
  });

export const addWrittenNewCompaniesToLeadBoard = (workflowInstanceId, stagingIds = null, options = {}) =>
  axios.post(`${BASE}/lead-board-add`, {
    workflow_instance_id: workflowInstanceId,
    ...(Array.isArray(stagingIds) && stagingIds.length ? { staging_ids: stagingIds } : {}),
    ...(options.boardId ? { board_id: options.boardId } : {}),
    ...(options.stageId ? { stage_id: options.stageId } : {}),
  });

/**
 * Remapează câmpuri pe un rând de staging.
 *
 * fieldUpdates = { sourceField: targetCrmColumn }
 *   ex: { instagram_profile_url: "short_name", username: "administrator_name" }
 *
 * saveToEndpoint (default true): salvează mapările și în endpoint.mapping_config_json
 *   pentru ca importurile viitoare să le folosească automat.
 *   Înregistrările vechi din staging nu se ating.
 */
export const remapStagingFields = (
  id,
  fieldUpdates,
  confirmedUnmapped = [],
  saveToEndpoint = true,
  workflowInstanceId = null
) =>
  axios.patch(`${BASE}/${id}/mapping`, {
    workflow_instance_id: workflowInstanceId,
    field_updates: fieldUpdates,
    confirmed_unmapped: confirmedUnmapped,
    save_to_endpoint: saveToEndpoint,
  });
